import axios from "axios";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { UsersTable } from "../../components/admin/UsersTable";
import Config from "../../Config";
import { useI18n } from "../../i18n/I18nProvider";
import {
  ADMIN_PERIODS,
  ADMIN_QUEUES,
  attachReferrers,
  buildAdminMetrics,
  buildMonthCash,
  classifyUsers,
  filterAdminUsers,
  getQueueUsers,
  listCashMonths,
  listCashYears,
} from "./adminMetrics";
import {
  AccessDenied,
  AdminHeader,
  AdminShell,
  AdminSubtitle,
  AdminTitle,
  CashPanel,
  CashSkip,
  CashSplit,
  CashTable,
  CashTableWrap,
  EmptyState,
  FunnelFill,
  FunnelList,
  FunnelRow,
  FunnelTrack,
  FunnelValue,
  InsightGrid,
  LoadingState,
  MetricCard,
  MetricGrid,
  MetricHint,
  MetricLabel,
  MetricValue,
  MonthPicker,
  MonthSelect,
  Panel,
  PanelHeader,
  PanelHint,
  PanelTitle,
  PeriodButton,
  PeriodSwitch,
  QueueCount,
  QueuePanel,
  QueueTab,
  QueueTabs,
  QueueTools,
  SearchInput,
  TariffCard,
  TariffLabel,
  TariffSplit,
  TariffValue,
} from "./AdminPage.styles";
import PersonalDefaultPage from "./PersonalDefaultPage";
import {
  canViewAdminFinance,
  isAdminUser,
} from "../../constants/adminAccess";

const formatRubles = (value) =>
  new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(value || 0);

const percentOf = (value, total) =>
  total > 0 ? Math.round((value / total) * 100) : 0;

const getQueueCount = (queueId, users, groups) =>
  getQueueUsers(queueId, users, groups).length;

export const AdminPage = () => {
  const admin = useSelector((state) => state.user);
  const { t } = useI18n();
  const timeZone = admin.time_zone || "Europe/Moscow";
  const hasAdminAccess = isAdminUser(admin.id);
  const canViewFinance = canViewAdminFinance(admin.id);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [periodId, setPeriodId] = useState("30d");
  const [queueId, setQueueId] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [cashYear, setCashYear] = useState(() =>
    String(moment().tz(timeZone).year()),
  );
  const [cashMonth, setCashMonth] = useState(() =>
    moment().tz(timeZone).format("MM"),
  );

  /**
   * Этот запрос только читает данные. Платежи прикрепляются к пользователям
   * для отчётов и истории, но сами записи и сроки подписок здесь не меняются.
   */
  const fetchData = useCallback(async () => {
    if (!hasAdminAccess) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const usersResponse = await axios.get(
        `${Config.BACKEND_ADDR}/auth/users`,
      );
      let payments = [];
      if (canViewFinance) {
        const paymentsResponse = await axios.get(
          `${Config.BACKEND_ADDR}/payments/allPayments`,
        );
        payments = Array.isArray(paymentsResponse.data)
          ? paymentsResponse.data
          : [];
      }

      const rawUsers = Array.isArray(usersResponse.data)
        ? usersResponse.data
        : [];
      const paymentsByUser = payments.reduce((result, payment) => {
        const userId = String(payment.user_id);
        result[userId] = [...(result[userId] || []), payment];
        return result;
      }, {});

      setPayments(payments);
      setUsers(
        attachReferrers(
          rawUsers
            .map((user) => ({
              ...user,
              payments: paymentsByUser[String(user.id)] || [],
            }))
            .sort((a, b) => Number(a.id) - Number(b.id)),
        ),
      );
    } catch (requestError) {
      console.error("Ошибка при получении данных админки:", requestError);
      setError(t("Не удалось загрузить пользователей и платежи"));
    } finally {
      setIsLoading(false);
    }
  }, [canViewFinance, hasAdminAccess, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const groups = useMemo(
    () => classifyUsers(users, timeZone),
    [timeZone, users],
  );
  const period =
    ADMIN_PERIODS.find((item) => item.id === periodId) || ADMIN_PERIODS[1];
  const metrics = useMemo(
    () =>
      buildAdminMetrics({
        users,
        groups,
        period,
        payments,
        timeZone,
      }),
    [groups, payments, period, timeZone, users],
  );
  const queueUsers = useMemo(
    () =>
      filterAdminUsers(
        getQueueUsers(queueId, users, groups),
        searchValue,
      ),
    [groups, queueId, searchValue, users],
  );
  const cashYears = useMemo(
    () => listCashYears(payments, timeZone),
    [payments, timeZone],
  );
  const cashMonths = useMemo(
    () => listCashMonths(cashYear, timeZone, t),
    [cashYear, t, timeZone],
  );
  const monthCash = useMemo(
    () =>
      buildMonthCash({
        payments,
        users,
        year: cashYear,
        month: cashMonth,
        timeZone,
        t,
      }),
    [cashMonth, cashYear, payments, t, timeZone, users],
  );

  useEffect(() => {
    if (!cashMonths.some((item) => item.value === cashMonth)) {
      setCashMonth(cashMonths[cashMonths.length - 1]?.value || "01");
    }
  }, [cashMonth, cashMonths]);

  const funnel = [
    {
      label: t("Все аккаунты"),
      value: metrics.totalUsers,
      color: "#f04f4f",
    },
    {
      label: t("Выбрали тариф"),
      value: metrics.selectedTariffCount,
      color: "#f27171",
    },
    {
      label: t("Когда-либо платили"),
      value: metrics.everPaidCount,
      color: "#ef9a76",
    },
    {
      label: t("Автосписание"),
      value: groups.active.length,
      color: "#17ad65",
    },
  ];

  if (!hasAdminAccess) {
    return (
      <PersonalDefaultPage>
        <AccessDenied>
          <h1>{t("Нет доступа")}</h1>
          <p>{t("Раздел доступен только администраторам SPEAKID.")}</p>
        </AccessDenied>
      </PersonalDefaultPage>
    );
  }

  return (
    <PersonalDefaultPage>
      <AdminShell>
        <AdminHeader>
          <div>
            <AdminTitle>{t("Админка")}</AdminTitle>
            <AdminSubtitle>
              {canViewFinance
                ? t(
                    "Часовой пояс: {{timeZone}}. Данные платежей используются только для отчётов.",
                    { timeZone },
                  )
                : t(
                    "Часовой пояс: {{timeZone}}. Доступ к пользователям и выдаче доступа без финансовых отчётов.",
                    { timeZone },
                  )}
            </AdminSubtitle>
          </div>
          {canViewFinance ? (
            <PeriodSwitch aria-label={t("Период отчёта")}>
              {ADMIN_PERIODS.map((item) => (
                <PeriodButton
                  key={item.id}
                  type="button"
                  $active={periodId === item.id}
                  onClick={() => setPeriodId(item.id)}
                >
                  {t(item.label)}
                </PeriodButton>
              ))}
            </PeriodSwitch>
          ) : null}
        </AdminHeader>

        {isLoading ? (
          <LoadingState>{t("Загрузка данных…")}</LoadingState>
        ) : error ? (
          <AccessDenied>
            <h1>{t("Данные не загрузились")}</h1>
            <p>{t("{{error}}. Обновите страницу или попробуйте позже.", { error })}</p>
          </AccessDenied>
        ) : (
          <>
            <MetricGrid>
              {canViewFinance ? (
                <>
                  <MetricCard>
                    <div>
                      <MetricLabel>{t("Расчётный MRR")}</MetricLabel>
                      <MetricValue>{formatRubles(metrics.mrrRubles)}</MetricValue>
                    </div>
                    <MetricHint>
                      {t(
                        "{{monthly}} мес. × 1 500 ₽ + {{yearly}} год. × 1 000 ₽/мес.",
                        {
                          monthly: metrics.activeTariffs.monthly,
                          yearly: metrics.activeTariffs.yearly,
                        },
                      )}
                    </MetricHint>
                  </MetricCard>
                  <MetricCard>
                    <div>
                      <MetricLabel>
                        {t("Выручка · {{period}}", { period: t(period.label) })}
                      </MetricLabel>
                      <MetricValue>
                        {formatRubles(metrics.revenueRubles)}
                      </MetricValue>
                    </div>
                    <MetricHint>
                      {t("{{count}} успешных (CONFIRMED + AUTHORIZED)", {
                        count: metrics.confirmedPayments,
                      })}
                    </MetricHint>
                  </MetricCard>
                </>
              ) : null}
              <MetricCard>
                <div>
                  <MetricLabel>{t("Пользователи с доступом")}</MetricLabel>
                  <MetricValue>{metrics.usersWithAccess}</MetricValue>
                </div>
                <MetricHint>
                  {t(
                    "{{active}} с автосписанием + {{cancelled}} отключили его",
                    {
                      active: groups.active.length,
                      cancelled: groups.cancelled.length,
                    },
                  )}
                </MetricHint>
              </MetricCard>
              {canViewFinance ? (
                <>
                  <MetricCard>
                    <div>
                      <MetricLabel>
                        {t("Проблемы оплаты · {{period}}", {
                          period: t(period.label),
                        })}
                      </MetricLabel>
                      <MetricValue>{metrics.rejectedPayments}</MetricValue>
                    </div>
                    <MetricHint $warning>
                      {t("REJECTED за период; в рабочей очереди {{count}}", {
                        count: groups.failed.length,
                      })}
                    </MetricHint>
                  </MetricCard>
                  <MetricCard>
                    <div>
                      <MetricLabel>{t("По рефералке")}</MetricLabel>
                      <MetricValue>{metrics.referredCount}</MetricValue>
                    </div>
                    <MetricHint>
                      {t(
                        "{{count}} уже оплатили тариф, не считая пробный платёж",
                        { count: metrics.referredPaidCount },
                      )}
                    </MetricHint>
                  </MetricCard>
                </>
              ) : null}
            </MetricGrid>

            {canViewFinance ? (
            <CashPanel>
              <PanelHeader>
                <div>
                  <PanelTitle>{t("Касса за месяц")}</PanelTitle>
                  <PanelHint>
                    {t(
                      "В сумму идут CONFIRMED и AUTHORIZED. AUTHORIZED — холд в Тинькофф, деньги уже взяты, Confirm иногда не доходит. pending, REJECTED и REFUNDED не считаем.",
                    )}
                  </PanelHint>
                </div>
                <MonthPicker>
                  <MonthSelect
                    aria-label={t("Год")}
                    value={cashYear}
                    onChange={(event) => setCashYear(event.target.value)}
                  >
                    {cashYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </MonthSelect>
                  <MonthSelect
                    aria-label={t("Месяц")}
                    value={cashMonth}
                    onChange={(event) => setCashMonth(event.target.value)}
                  >
                    {cashMonths.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </MonthSelect>
                </MonthPicker>
              </PanelHeader>
              <CashSplit>
                <TariffCard $background="#fff1ef">
                  <TariffValue>
                    {formatRubles(monthCash.revenueRubles)}
                  </TariffValue>
                  <TariffLabel>{t("Сумма оплат")}</TariffLabel>
                </TariffCard>
                <TariffCard $background="#f7f6f4">
                  <TariffValue>{monthCash.confirmedCount}</TariffValue>
                  <TariffLabel>{t("Подтверждённых оплат")}</TariffLabel>
                </TariffCard>
                <TariffCard $background="#eafbf3">
                  <TariffValue>{monthCash.tariffCount}</TariffValue>
                  <TariffLabel>
                    {t("Тарифных, без пробных ~1 ₽")}
                    {monthCash.trialCount > 0
                      ? t(" · пробных {{count}}", {
                          count: monthCash.trialCount,
                        })
                      : ""}
                  </TariffLabel>
                </TariffCard>
              </CashSplit>
              {monthCash.skipped.length > 0 ||
              monthCash.undatedConfirmed.length > 0 ? (
                <PanelHint style={{ marginTop: 12 }}>
                  {t("Не в сумме: {{count}} с другой причиной", {
                    count: monthCash.skipped.length,
                  })}
                  {monthCash.undatedConfirmed.length > 0
                    ? t(", {{count}} успешных без даты", {
                        count: monthCash.undatedConfirmed.length,
                      })
                    : ""}
                </PanelHint>
              ) : null}
              {monthCash.rows.length > 0 ? (
                <CashTableWrap>
                  <CashTable>
                    <thead>
                      <tr>
                        <th>{t("Дата")}</th>
                        <th>{t("Сумма")}</th>
                        <th>{t("Статус")}</th>
                        <th>{t("Кто")}</th>
                        <th>{t("В сумме?")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthCash.rows.map((row) => (
                        <tr key={row.id || row.paymentId}>
                          <td>{row.dateLabel}</td>
                          <td>
                            {row.amountRubles === null
                              ? "—"
                              : formatRubles(row.amountRubles)}
                          </td>
                          <td>{row.status}</td>
                          <td>
                            {row.userName}
                            {row.userId ? ` · ${row.userId}` : ""}
                            {row.userMissing ? t(" (нет в базе)") : ""}
                          </td>
                          <td>
                            <CashSkip $ok={row.counted}>
                              {row.counted ? t("да") : row.skipReason}
                            </CashSkip>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </CashTable>
                </CashTableWrap>
              ) : (
                <PanelHint style={{ marginTop: 16 }}>
                  {t("За этот месяц в allPayments записей нет")}
                </PanelHint>
              )}
              {monthCash.undatedConfirmed.length > 0 ? (
                <PanelHint style={{ marginTop: 12 }}>
                  {t("Успешные без даты:")}{" "}
                  {monthCash.undatedConfirmed
                    .map(
                      (row) =>
                        `${formatRubles(row.amountRubles || 0)} · user ${row.userId || "—"}`,
                    )
                    .join("; ")}
                </PanelHint>
              ) : null}
            </CashPanel>
            ) : null}

            {canViewFinance ? (
            <InsightGrid>
              <Panel>
                <PanelHeader>
                  <div>
                    <PanelTitle>{t("Воронка базы")}</PanelTitle>
                    <PanelHint>
                      {t(
                        "Текущий срез; регистрации по периоду потребуют created_at",
                      )}
                    </PanelHint>
                  </div>
                </PanelHeader>
                <FunnelList>
                  {funnel.map((item) => {
                    const percent = percentOf(
                      item.value,
                      metrics.totalUsers,
                    );
                    return (
                      <FunnelRow key={item.label}>
                        <span>{item.label}</span>
                        <FunnelTrack>
                          <FunnelFill
                            $percent={percent}
                            $color={item.color}
                          />
                        </FunnelTrack>
                        <FunnelValue>
                          {item.value} · {percent}%
                        </FunnelValue>
                      </FunnelRow>
                    );
                  })}
                </FunnelList>
              </Panel>

              <Panel>
                <PanelHeader>
                  <div>
                    <PanelTitle>{t("Живые автосписания")}</PanelTitle>
                    <PanelHint>
                      {t("{{count}} пользователей", {
                        count: groups.active.length,
                      })}
                    </PanelHint>
                  </div>
                </PanelHeader>
                <TariffSplit>
                  <TariffCard $background="#fff1ef">
                    <TariffValue>
                      {metrics.activeTariffs.monthly}
                    </TariffValue>
                    <TariffLabel>{t("Месячный тариф")}</TariffLabel>
                  </TariffCard>
                  <TariffCard $background="#eafbf3">
                    <TariffValue>
                      {metrics.activeTariffs.yearly}
                    </TariffValue>
                    <TariffLabel>{t("Годовой тариф")}</TariffLabel>
                  </TariffCard>
                </TariffSplit>
              </Panel>
            </InsightGrid>
            ) : null}

            <QueuePanel>
              <PanelHeader>
                <div>
                  <PanelTitle>{t("Очереди для работы")}</PanelTitle>
                  <PanelHint>
                    {t(
                      "Статусы описывают текущее состояние, а не обещают причину",
                    )}
                  </PanelHint>
                </div>
              </PanelHeader>

              <QueueTabs>
                {ADMIN_QUEUES.map((queue) => (
                  <QueueTab
                    key={queue.id}
                    type="button"
                    $active={queueId === queue.id}
                    onClick={() => setQueueId(queue.id)}
                  >
                    {t(queue.label)} · {getQueueCount(queue.id, users, groups)}
                  </QueueTab>
                ))}
              </QueueTabs>

              <QueueTools>
                <SearchInput
                  type="search"
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder={t("Имя, email, телефон, ID, код или кто привёл")}
                  aria-label={t("Поиск пользователей")}
                />
                <QueueCount>
                  {t("Показано {{shown}} из {{total}}", {
                    shown: queueUsers.length,
                    total: getQueueCount(queueId, users, groups),
                  })}
                </QueueCount>
              </QueueTools>

              {queueUsers.length > 0 ? (
                <UsersTable
                  users={queueUsers}
                  clientType={queueId}
                  onFetchData={fetchData}
                  canViewFinance={canViewFinance}
                />
              ) : (
                <EmptyState>{t("В этой очереди никого нет")}</EmptyState>
              )}
            </QueuePanel>
          </>
        )}
      </AdminShell>
    </PersonalDefaultPage>
  );
};
